import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useSalesForceIntegrationQuery } from "modules/settings/hooks/salesForce/useSalesForceIntegrationQuery"
import { useUpsertSalesForceIntegrationMutation } from "modules/settings/hooks/salesForce/useUpsertSalesForceIntegrationMutation"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertSalesForceIntegrationBodySchema } from "schema"
import { z } from "zod"

export const SalesForceAuthModal = () => {
  const { t } = useTranslation()
  const { data } = useSalesForceIntegrationQuery()
  const form = useForm<z.infer<typeof UpsertSalesForceIntegrationBodySchema>>({
    resolver: zodResolver(UpsertSalesForceIntegrationBodySchema),
    defaultValues: data ?? {},
  })
  const navigate = useNavigate()
  const { mutateAsync } = useUpsertSalesForceIntegrationMutation()

  const onSubmit = async (data: z.infer<typeof UpsertSalesForceIntegrationBodySchema>) => {
    await mutateAsync(data)
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/salesForce" })
    }
  }

  useEffect(() => {
    if (data) {
      form.reset(data)
    }
  }, [data, form])

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.salesForce.authModal.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.salesForce.authModal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <div className="flex flex-col w-full gap-4">
              <FormField
                control={form.control}
                name="baseUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.salesForce.authModal.form.baseUrl")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} required type="url" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.salesForce.authModal.form.clientId")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientSecret"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.salesForce.authModal.form.clientSecret")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="redirectUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.salesForce.authModal.form.redirectUrl")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} required type="url" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="refreshToken"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.salesForce.authModal.form.refreshToken")}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button disabled={form.formState.isLoading || !form.formState.isValid}>
                {t("settings.integrations.salesForce.authModal.form.submit")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
